import { z } from "zod";
import dayjs, { type Dayjs } from "dayjs";
import { parseAsArrayOf, parseAsString, parseAsStringLiteral } from "nuqs";
import type { CreateBookForMePayload } from "@/actions/book-for-me.action";
import type { UseHandleSearchHotelResultsParams } from "@/hooks/hotel/useHotelSearchResults";
import type {
  HotelFilterCriteriaAttributeItem,
  HotelFilterCriteriaData,
} from "@/types/api";

export const BOOK_FOR_ME_STEPS = ["form", "finding", "results"] as const;
export type BookForMeStep = (typeof BOOK_FOR_ME_STEPS)[number];

const parseQueryString = parseAsString.withOptions({ clearOnDefault: true });
const parseQueryStringList = parseAsArrayOf(parseAsString).withOptions({
  clearOnDefault: true,
});

export const bookForMeQueryParsers = {
  step: parseAsStringLiteral(BOOK_FOR_ME_STEPS)
    .withDefault("form")
    .withOptions({ clearOnDefault: true }),
  destination: parseQueryString,
  destinationId: parseQueryString,
  date: parseQueryString,
  guests: parseQueryString,
  budgetMin: parseQueryString,
  budgetMax: parseQueryString,
  preferences: parseQueryStringList,
  additionalRequests: parseQueryString,
};

/** Separator used by FormDateRangeField: "DD MMM YYYY - DD MMM YYYY" */
const DATE_RANGE_SEPARATOR = " - ";
const API_DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_PREFER_RES_IN_MIN = "15";

export type BookForMeGuests = {
  adults: number;
  children: number;
  rooms: number;
};

const toValidDate = (value?: string): Dayjs | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const date = dayjs(trimmed);
  return date.isValid() ? date : undefined;
};

/**
 * Parse the date range string produced by FormDateRangeField.
 */
export const parseDateRange = (
  value?: string,
): { from?: Dayjs; to?: Dayjs } => {
  const [from, to] = (value ?? "").split(DATE_RANGE_SEPARATOR);
  return { from: toValidDate(from), to: toValidDate(to) };
};

/**
 * Parse the guest summary string produced by FormTravelerField
 * (e.g. "2 adults · 1 child · 1 room"). Missing parts resolve to 0.
 */
export const parseGuests = (value?: string): BookForMeGuests => {
  const count = (re: RegExp) => Number(value?.match(re)?.[1] ?? 0);
  return {
    adults: count(/(\d+)\s*adult/i),
    children: count(/(\d+)\s*child/i),
    rooms: count(/(\d+)\s*room/i),
  };
};

export const toApiDate = (date?: Dayjs) =>
  date ? date.format(API_DATE_FORMAT) : "";

export type BookForMePreferenceId =
  | "breakfast"
  | "pool"
  | "wifi"
  | "pet_friendly"
  | "free_parking"
  | "air_conditioning"
  | "fitness_center";

export type PreferenceIconName =
  "coffee" | "pool" | "wifi" | "paw" | "parking" | "ac" | "barbell";

export type BookForMePreferenceOption = {
  id: BookForMePreferenceId;
  label: string;
  iconName: PreferenceIconName;
  /** Backend attribute id sent as `preferAttributes` */
  attributeId?: string;
};

export const bookForMeFormSchema = z
  .object({
    destination: z.string(),
    destinationId: z.string().min(1, "Please select your destination"),
    date: z
      .string()
      .min(1, "Please select check-in & check-out date")
      .superRefine((value, ctx) => {
        const { from, to } = parseDateRange(value);
        if (!from || !to) {
          ctx.addIssue({
            code: "custom",
            message: "Please select both check-in & check-out date",
          });
          return;
        }
        if (from.isBefore(dayjs(), "day")) {
          ctx.addIssue({
            code: "custom",
            message: "Check-in date cannot be in the past",
          });
          return;
        }
        if (!to.isAfter(from, "day")) {
          ctx.addIssue({
            code: "custom",
            message: "Check-out must be after check-in date",
          });
        }
      }),
    guests: z.string().superRefine((value, ctx) => {
      const { adults, rooms } = parseGuests(value);
      if (adults < 1) {
        ctx.addIssue({
          code: "custom",
          message: "At least 1 adult is required",
        });
      } else if (rooms < 1) {
        ctx.addIssue({
          code: "custom",
          message: "At least 1 room is required",
        });
      } else if (rooms > adults) {
        ctx.addIssue({
          code: "custom",
          message: "Each room needs at least 1 adult",
        });
      }
    }),
    budgetMin: z
      .string()
      .refine(
        (v) => !v.trim() || (!Number.isNaN(Number(v)) && Number(v) >= 0),
        "Please enter a valid minimum budget",
      ),
    budgetMax: z
      .string()
      .refine(
        (v) => !v.trim() || (!Number.isNaN(Number(v)) && Number(v) >= 0),
        "Please enter a valid maximum budget",
      ),
    preferences: z.array(z.string()),
    additionalRequests: z.string(),
  })
  .superRefine(({ budgetMin, budgetMax }, ctx) => {
    const min = budgetMin.trim() ? Number(budgetMin) : undefined;
    const max = budgetMax.trim() ? Number(budgetMax) : undefined;

    if (
      min !== undefined &&
      max !== undefined &&
      !Number.isNaN(min) &&
      !Number.isNaN(max) &&
      max < min
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["budgetMax"],
        message: "Maximum budget cannot be less than minimum budget",
      });
    }
  });

export type BookForMeFormData = {
  destination: string;
  destinationId: string;
  date: string;
  guests: string;
  budgetMin: string;
  budgetMax: string;
  preferences: string[];
  additionalRequests: string;
};

export const extractFacilityAttributes = (
  criteria?: HotelFilterCriteriaData,
): HotelFilterCriteriaAttributeItem[] => {
  const groups = criteria?.attributes ?? [];
  return groups
    .filter((group) => {
      const name = String(group.name ?? "")
        .trim()
        .toLowerCase();
      return name.includes("facility") || name.includes("facilities");
    })
    .flatMap((group) => group.attributes ?? []);
};

export const buildBookForMePayload = (
  data: BookForMeFormData,
): CreateBookForMePayload => {
  const { from, to } = parseDateRange(data.date);
  const { adults, children, rooms } = parseGuests(data.guests);

  return {
    destinationId: data.destinationId,
    budget: { max: data.budgetMax, min: data.budgetMin },
    guest: {
      adultCount: String(adults || 2),
      childCount: String(children),
      roomCount: String(rooms || 1),
    },
    preferAttributes: data.preferences.filter(Boolean).join(","),
    preferResInMin: DEFAULT_PREFER_RES_IN_MIN,
    checkInDate: toApiDate(from),
    checkOutDate: toApiDate(to),
    additionalReqs: data.additionalRequests,
  };
};

export const buildBookForMeSearchParams = (
  data: BookForMeFormData,
): UseHandleSearchHotelResultsParams => {
  const { from, to } = parseDateRange(data.date);
  const { adults, children, rooms } = parseGuests(data.guests);
  const minPrice = Number(data.budgetMin);
  const maxPrice = Number(data.budgetMax);
  const priceRange =
    Number.isFinite(minPrice) && Number.isFinite(maxPrice) && maxPrice > 0
      ? [minPrice || 0, maxPrice]
      : [];

  const facilities = data.preferences.filter(Boolean);

  return {
    locationId: data.destinationId,
    search: data.destinationId ? "" : data.destination,
    start: toApiDate(from),
    end: toApiDate(to),
    tab: "popular",
    sortBy: "default",
    adults: adults || 2,
    children,
    rooms: rooms || 1,
    priceRange,
    propertyType: [],
    propertyRating: [],
    numberOfBeds: [],
    facilities,
    distance: [],
  };
};

export const DEFAULT_BOOK_FOR_ME_FORM_VALUES: BookForMeFormData = {
  destination: "",
  destinationId: "",
  date: "",
  guests: "2 adults · 1 child · 1 room",
  budgetMin: "",
  budgetMax: "",
  preferences: [],
  additionalRequests: "",
};

export type HowItWorksStep = {
  step: number;
  title: string;
  description: string;
  iconName: "file" | "search" | "chat" | "check";
};

export const MOCK_HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    step: 1,
    title: "Submit your request",
    description: "Tell us your trip details and hotel preferences.",
    iconName: "file",
  },
  {
    step: 2,
    title: "Our team finds the best options",
    description: "Tovtrip will search and select suitable hotels for you.",
    iconName: "search",
  },
  {
    step: 3,
    title: "Get personalized recommendations",
    description: "We'll share the options with you based on your needs.",
    iconName: "chat",
  },
  {
    step: 4,
    title: "You choose and book",
    description: "Pick your favorite and complete this booking.",
    iconName: "check",
  },
];
